import { Customer_ContractualAccounts } from 'data/endpoints';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import IAppState from 'infrastructure/store/IAppState';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BasicInfoSubFormSection from '../basicInfoSubForm/BasicInfoSubFormSection';
import CreateRequirementResultSubDialog from 'features/requirements/components/CreateRequirementResultSubDialog';
import { useQuery } from 'data/hooks/useQuery';
import DataQuery from 'data/DataQuery';
import ContractualAccountDTO from 'features/contracts/dtos/ContractualAccountDTO';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import ContractualAccountSelect from 'features/contracts/components/ContractualAccountSelect';
import { Nullable } from 'features/shared/types/nullable';
import { paymentMethodValidations, validatePaymentMethods } from './paymentMethodValidation';
import IPaymentMethodFormService from 'features/requirements/services/IPaymentMethodFormService';
import { Currency } from 'features/shared/enums/currency';
import PaymentMethodSubForm from 'features/requirements/components/paymentMethodForm/PaymentMethodSubForm';
import PaymentMethodDTO from 'features/payments/dtos/PaymentMethodDTO';
import { PaymentMethodType } from 'features/payments/enums/paymentMethodType';
import CustomerBankAccountDTO from 'features/customer/dtos/CustomerBankAccountDTO';
import PaymentMethodTypeEnumDTO from 'features/shared/enums/dynamic/PaymentMethodTypeEnumDTO';
import IRequirementFormService from 'features/requirements/services/IRequirementFormService';
import PaymentMethodCategoryDTO from 'features/payments/dtos/PaymentMethodCategoryDTO';
import CurrencySelect from 'features/requirements/components/paymentMethodForm/CurrencySelect';
import CustomerSipoAccountDTO from 'features/customer/dtos/CustomerSipoAccountDTO';
import { paymentCategoriesMapping } from './paymentCategoriesMapping';
import BankAccountDialog from 'features/payments/components/bankAccount/BankAccountDialog';
import { formatBankAccount } from 'features/shared/helpers/Formatters';
import SipoAccountDialog from 'features/payments/components/sipoAccount/SipoAccountDialog';
import BankTypeEnumDTO from 'features/shared/enums/dynamic/BankTypeEnumDTO';
import { trackPromise } from 'react-promise-tracker';
import { PaymentMethodFormSection_PageCard } from 'features/shared/components/loaders/promiseLoadingAreas';
import PagedDataResultDTO from 'features/shared/dtos/PagedDataResultDTO';
import { getContractualAccountsParams } from 'features/shared/helpers/queryParams';
import { useTranslation } from 'react-i18next';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';

const PaymentMethodFormSection = () => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  //#endregion translations

  //#region  DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const formService = useInjection<IPaymentMethodFormService>(IocTypes.PaymentMethodFormService);
  const baseFormService = useInjection<IRequirementFormService>(IocTypes.RequirementFormService);
  //#endregion DI

  //#region store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;
  //#endregion store

  //#region state
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [paymentMethodTypes, setPaymentMethodTypes] = useState<PaymentMethodTypeEnumDTO[]>([]);
  const [bankAccounts, setBankAccounts] = useState<CustomerBankAccountDTO[]>([]);
  const [sipos, setSipos] = useState<CustomerSipoAccountDTO[]>([]);
  const [banks, setBanks] = useState<BankTypeEnumDTO[]>([]);
  const [showBankAccountDialog, setShowBankAccountDialog] = useState(false);
  const [showSipoAccountDialog, setShowSipoAccountDialog] = useState(false);
  const [selectedContractulAccount, setSelectedContractulAccount] = useState<Nullable<ContractualAccountDTO>>(null);
  const [acualPaymentCategory, setAcualPaymentCategory] = useState<string>("");
  //#endregion state

  //#region fetch data
  let accountsQuery: DataQuery = {
    url: Customer_ContractualAccounts,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: getContractualAccountsParams()
  };
  const { data: contractualAccounts, loadStatus: accountsStatus } = useQuery<PagedDataResultDTO<ContractualAccountDTO>>(accountsQuery, { count: 0, result: [] }, [customer]);
  //#endregion fetch data

  //#region form hook
  const {
    values,
    errors,
    bindField,
    bindUncontrolledField,
    valideteForm,
    validateFormField,
    setFormValue,
    setValidationRule,
    setValidationError,
    clearValidationErrors,
    clearForm
  } = useForm({
    initialValidations: paymentMethodValidations()
  });
  //#endregion form hook

  //#region side-effects
  useEffect(() => {
    // set default contract
    if (contractualAccounts.result.length === 1) {
      contractualAccountChangeHandler(contractualAccounts.result[0]);
    }
  }, [contractualAccounts]);
  //#endregion

  //#region helpers
  const fetchDataByCurrency = async (currency: Currency) => {
    let fetchedPaymentMethodTypes = await formService.fetchPaymentMethodTypes(customer.customerId!, currency);
    let fetchedBankAccounts = await formService.fetchBankAccounts(customer.customerId!, currency);
    let fetchedSipos = await formService.fetchSipos(customer.customerId!, currency);

    return {
      fetchedPaymentMethodTypes,
      fetchedBankAccounts,
      fetchedSipos
    }
  }
  //#endregion helpers

  //#region handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    if (valideteForm() && validatePaymentMethods(values, paymentMethodTypes, setValidationError)) {
      let result = await createService.createRequirement("ZMENAZPUSOBUPLATBY", values);
      openRequirementResultDialog(false, { response: result });
    }
  };

  const contractualAccountChangeHandler = (contractulAccount: Nullable<ContractualAccountDTO>) => {
    if (validateFormField("DESC_TMSmluvniUcetVS", contractulAccount) && contractulAccount) {
      // currencies array
      let uniqueCurrencies = [...new Set([contractulAccount.currencyDistribution!, contractulAccount.currencySales!])];

      // set state
      setSelectedContractulAccount(contractulAccount);
      setCurrencies(uniqueCurrencies);
      
      // set currency (if is one currency)
      if (uniqueCurrencies.length === 1) {
        currencyChangeHandler(uniqueCurrencies[0], contractulAccount.contractualAccountId);
      } else {
        // clear previous value
        baseFormService.clearFormValues(["DESC_TMMena"], setFormValue);
      }

      // set form values
      setFormValue("DESC_TMSmluvniUcetVS", contractulAccount?.contractualAccountId);       
    }
  }

  const currencyChangeHandler = async (currency: Nullable<Currency>, contractualAccountId?: string) => {
    if (validateFormField("DESC_TMMena", currency) && currency) {
      let accountId = contractualAccountId ?? selectedContractulAccount?.contractualAccountId!; // because state update is async (for call from contractualAccountChangeHandler)
      
      // fetch actual payment methods
      let paymentMethods = await trackPromise(
        formService.fetchPaymentMethods(
          customer.customerId!,
          accountId,
          currency
        ),
        PaymentMethodFormSection_PageCard
      );
      
      // load data from state
      let loadPaymentMethodTypes = paymentMethodTypes;
      let loadBankAccounts= bankAccounts;
      let loadSipos = sipos

      if (values["DESC_TMMena"] !== currency) {
        // fetch new data only if is currency different than previous
        const {
          fetchedPaymentMethodTypes,
          fetchedBankAccounts,
          fetchedSipos,
        } = await trackPromise(
          fetchDataByCurrency(currency),
          PaymentMethodFormSection_PageCard
        );

        loadPaymentMethodTypes = fetchedPaymentMethodTypes;
        loadBankAccounts = fetchedBankAccounts;
        loadSipos = fetchedSipos;

        // set data to state
        setPaymentMethodTypes(loadPaymentMethodTypes);
        setBankAccounts(loadBankAccounts);
        setSipos(loadSipos);
      }

      // set defaults
      formService.setPaymentMethodsDefaults(
        paymentMethods,
        loadPaymentMethodTypes,
        loadBankAccounts,
        loadSipos,
        setFormValue
      );

      // set form values
      setFormValue("DESC_TMMena", currency);   
    }
  }
  
  const paymentMethodChangeHandler = (category: Nullable<PaymentMethodCategoryDTO>, paymentMethodType: string) => {
    let descPrefix = paymentCategoriesMapping[paymentMethodType];
    
    if (category) {
      // clear previous values
      formService.clearPreviousBankAccount(paymentMethodType, setFormValue);
      baseFormService.clearFormValues([`${descPrefix}CisloSipo`,], setFormValue);

      // set form value
      setFormValue(`${descPrefix}Zpusob`, category?.paymentMethodTypeId);  
      
      // clear validation errors (if exists)
      clearValidationErrors([`${descPrefix}Zpusob`, `${descPrefix}BankUcet`, `${descPrefix}CisloSipo`]);
    }
  }
  
  const bankAccountChangeHandler = (bankAccount: Nullable<CustomerBankAccountDTO>, paymentMethodType: string) => {
    let descName = `${paymentCategoriesMapping[paymentMethodType]}BankUcet`;
    
    if (bankAccount) {
      formService.clearPreviousBankAccount(paymentMethodType, setFormValue);

      if (bankAccount.accountNumberId) {
        // existing account
        setFormValue(descName, bankAccount.accountNumberId);      
      } else {
        // new account
        formService.setNewBankAccount(bankAccount, paymentMethodType, setFormValue);
      }

      // clear validation error (if exists)
      clearValidationErrors([descName]);
    } 
  }
  
  const sipoChangeHandler = (sipo: Nullable<CustomerSipoAccountDTO>, paymentMethodType: string) => {
    let descPrefix = paymentCategoriesMapping[paymentMethodType];

    if (sipo) {
      formService.clearPreviousSipoAccount(paymentMethodType, setFormValue);

      if (sipo.sipoNumberId) {
        // existing account
        setFormValue(`${descPrefix}CisloSipo`, sipo.sipoNumberId);
      } else {
        // new account
        setFormValue(`${descPrefix}SpojCislo`, sipo.sipoNumber); 
      }

      // clear validation error (if exists)
      clearValidationErrors([`${descPrefix}CisloSipo`]);
    } 
  }

  const bankAccountAddHandler = (bankAccount: CustomerBankAccountDTO) => {
    let existingAccount = bankAccounts.find(x => formatBankAccount(x) === formatBankAccount(bankAccount));

    if (existingAccount === undefined) {
      let newBankAccounts = [...bankAccounts, bankAccount];
      setBankAccounts(newBankAccounts);
    } else {
      bankAccount = existingAccount;
    }

    // select created bank account
    if (acualPaymentCategory) {
      bankAccountChangeHandler(bankAccount, acualPaymentCategory);
    }

    setShowBankAccountDialog(false);
  }

  const sipoAccountAddHandler = (sipoAccount: CustomerSipoAccountDTO) => {
    let existingAccount = sipos.find(x => x.sipoNumber === sipoAccount.sipoNumber);

    if (existingAccount === undefined) {
      let newSipos = [...sipos, sipoAccount];
      setSipos(newSipos);
    } else {
      sipoAccount = existingAccount;
    }

    // select created sipo account
    if (acualPaymentCategory) {
      sipoChangeHandler(sipoAccount, acualPaymentCategory);
    }

    setShowSipoAccountDialog(false);
  }

  const openBankAccountDialogHandler = async (paymentCategory?: string) => {
    if (banks.length == 0) {
      let fetchedBanks = await trackPromise(formService.fetchBanks());
      setBanks(fetchedBanks);
    }  

    setShowBankAccountDialog(true);

    if (paymentCategory) {
      setAcualPaymentCategory(paymentCategory);
    }
  }

  const openSipoAccountDialogHandler = (paymentCategory?: string) => {
    setShowSipoAccountDialog(true);

    if (paymentCategory) {
      setAcualPaymentCategory(paymentCategory);
    }
  }
  //#endregion handlers

  //#region sub-renders
  const renderSubForms = () => {
    if (baseFormService.isFilledValues(values, ["DESC_TMMena"])) {
      return (
        <>
          <h5 className="my-3 text-muted">{tLabels("paymentMethodFormSection.paymentMethods")}</h5>
          <div className="row no-gutters mx-n2">
            {paymentMethodTypes.map((methodType, index) =>
              <div className="col-xl-4 px-2" key={index}>
                <PaymentMethodSubForm 
                  paymentMethodType={methodType}
                  bankAccounts={bankAccounts}
                  sipos={sipos}
                  values={values}
                  errors={errors}
                  onPaymentMethodChange={paymentMethodChangeHandler}
                  onBankAccountChange={bankAccountChangeHandler}
                  onSipoChange={sipoChangeHandler}
                  onBankAccountAddClick={openBankAccountDialogHandler}
                  onSipoAccountAddClick={openSipoAccountDialogHandler}
                />
              </div>
              )}
          </div>
        </>
      );
    }
  }
  //#endregion sub-renders

  //#region render
  return (
    <NoContentWrapper
      data={contractualAccounts}
      loadStatus={accountsStatus}
      noContentInfo={tLabels("paymentMethodFormSection.contractualAccountNotFound")}
      noContentAsAlert
    >
      <form onSubmit={submitFormHandler}>
        <PageCard 
          title={tLabels("paymentMethodFormSection.cardTitle")}
          bodyClassName="pt-4 px-4"
          loadingAreaName={PaymentMethodFormSection_PageCard}
          loadingDelay={0}
        >
          <div className="row no-gutters mx-n2">
            <div className="col-lg-6 px-2">
              <ContractualAccountSelect 
                name="DESC_TMSmluvniUcetVS"
                contractualAccounts={contractualAccounts.result}
                onChange={contractualAccountChangeHandler}
                selectedContractualAccount={selectedContractulAccount}
                errors={errors}
              />
            </div>
            <div className="col-lg-6 px-2">
              <CurrencySelect 
                name="DESC_TMMena"
                currencies={currencies}
                selectedCurrency={values["DESC_TMMena"] ?? null}
                onChange={(c) => currencyChangeHandler(c)}
                visible={baseFormService.isFilledValues(values, ["DESC_TMSmluvniUcetVS"])}
                errors={errors}
              />
            </div>
          </div>       
        </PageCard>

        {renderSubForms()}
        
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />

        <button type="submit" className="btn btn-primary px-3 py-2">{tLabels("paymentMethodFormSection.sendRequirements")}</button>
      </form>

      <BankAccountDialog 
        show={showBankAccountDialog}
        setShow={setShowBankAccountDialog}
        onSubmit={bankAccountAddHandler}
        banks={banks}
      />

      <SipoAccountDialog 
        show={showSipoAccountDialog}
        setShow={setShowSipoAccountDialog}
        onSubmit={sipoAccountAddHandler}
      />
    </NoContentWrapper>
  );
  //#endregion render
}

export default PaymentMethodFormSection;
